import { memo, useCallback, useState, KeyboardEvent, ChangeEvent } from 'react';

import { useTranslations } from 'next-intl';
import { ControllerRenderProps, useFormContext } from 'react-hook-form';

import PenIcon from '@/assets/icons/pen.svg';
import ProfileIcon from '@/assets/icons/profile.svg';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { T_ProfileFormData } from '@/lib/schemas/profileForm.schema';
import { cn } from '@/lib/utils/cn';
import { E_ProfileForm } from '@/ts/enums/E_ProfileForm';

interface I_ProfileInformationInputProps {
  field: ControllerRenderProps<T_ProfileFormData, E_ProfileForm.NICKNAME>;
}

const ProfileInformationInput = memo(function ProfileInformationInput({
  field
}: I_ProfileInformationInputProps) {
  const t = useTranslations('editProfile.form');
  const [isEditing, setIsEditing] = useState(false);
  const { getFieldState } = useFormContext<T_ProfileFormData>();
  const fieldState = getFieldState(E_ProfileForm.NICKNAME);
  const hasError = !!fieldState.error;

  const handleToggleEdit = useCallback(() => {
    if (isEditing) {
      // Trigger validation by calling onChange with current value
      // Zod will handle trimming automatically
      field.onChange(field.value);
    }
    setIsEditing(!isEditing);
  }, [isEditing, field]);

  const handleKeyPress = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleToggleEdit();
      }
      if (e.key === 'Escape') {
        setIsEditing(false);
      }
    },
    [handleToggleEdit]
  );

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      // Update field value directly in real-time for validation
      field.onChange(e.target.value);
    },
    [field]
  );

  return (
    <div className="w-full">
      <div
        className={cn(
          'flex border items-center pl-3 h-9 bg-accordion-brand rounded-lg w-full',
          hasError && 'border-destructive'
        )}
      >
        <ProfileIcon className="mr-3  text-foreground" />
        <Input
          placeholder={t('username')}
          value={field.value}
          onChange={handleInputChange}
          onKeyDown={handleKeyPress}
          disabled={!isEditing}
          className={cn(
            'outline-none border-0 ring-0! shadow-none p-0 disabled:cursor-default',
            hasError && 'text-destructive'
          )}
        />
        <Button
          className={cn(
            'rounded-tl-none rounded-bl-none hover:bg-transparent hover:opacity-60',
            hasError ? 'border-l-destructive' : 'border-l-card'
          )}
          variant="ghost"
          onClick={handleToggleEdit}
          icon={<PenIcon className="text-foreground" />}
        />
      </div>
    </div>
  );
});

export default ProfileInformationInput;
